/**
 * §B6 / plan PR-7 — where this account is signed in, as an Account section.
 *
 * One row per refresh-token family, which is the server's word for "one
 * signed-in device or browser". The fingerprint column shows hash *prefixes*,
 * never an address or a user-agent string — the API deliberately sends only
 * enough to tell two sessions apart, and this panel must not imply it knows
 * more than that.
 *
 * **Revoking the current session signs you out.** The confirmation says so
 * before it happens, and afterwards the client routes through the ordinary
 * `logout()` — the server has already killed the family and cleared the
 * cookie, so anything short of a full local sign-out would leave the shell
 * rendering an account it no longer has.
 *
 * **A 404 hides the panel entirely** (plan §A PR-7: mergeable before the
 * sessions routes land). Hidden, not an error state: an operator cannot act
 * on "this deployment's API predates session visibility", so the honest
 * rendering is the one from before the feature existed — logged through
 * `reportProblem` so the gap is visible to us without alarming them.
 */

import type { SessionSummary } from '@rd/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';

import { useAuth } from '../../auth/AuthProvider.js';
import { Badge } from '../../components/Badge.js';
import { Button } from '../../components/Button.js';
import { ErrorState } from '../../components/ErrorState.js';
import { ConfirmDialog } from '../../components/Modal.js';
import { Skeleton } from '../../components/Skeleton.js';
import { Table, type Column } from '../../components/Table.js';
import { useToast } from '../../components/Toast.js';
import { apiRequest, ApiClientError } from '../../lib/apiClient.js';
import { reportProblem } from '../../lib/report.js';

export const SESSIONS_KEY = ['auth', 'sessions'] as const;

function when(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString();
}

/** `a1b2c3d4… · 5e6f` — enough to tell sessions apart, never more. */
function fingerprint(session: SessionSummary): string {
  const parts = [session.userAgentHash?.slice(0, 8), session.ipHashPrefix].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export function SessionsPanel(): ReactElement | null {
  const { logout } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [revoking, setRevoking] = useState<SessionSummary | null>(null);

  const query = useQuery<SessionSummary[]>({
    queryKey: SESSIONS_KEY,
    queryFn: () => apiRequest<SessionSummary[]>('/auth/sessions'),
  });

  const revoke = useMutation<{ revoked: boolean; current: boolean }, unknown, SessionSummary>({
    mutationFn: (session) =>
      apiRequest<{ revoked: boolean; current: boolean }>(`/auth/sessions/${session.familyId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_result, session) => {
      void queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
      if (session.current) {
        // The server already revoked the family and cleared the cookie; the
        // local half of the sign-out must follow or the shell keeps rendering
        // a session that no longer exists.
        void logout();
        return;
      }
      toast.push('success', 'That device was signed out.');
    },
  });

  // See the header: an API without the sessions routes yet answers 404, and
  // the panel renders as if the feature does not exist — because here, it
  // does not. Logged so the gap is ours to see, not theirs to worry about.
  const notFound =
    query.error instanceof ApiClientError && query.error.httpStatus === 404;
  useEffect(() => {
    if (notFound) reportProblem('sessions', 'GET /auth/sessions answered 404 — panel hidden');
  }, [notFound]);
  if (notFound) return null;

  const confirmRevoke = async (): Promise<void> => {
    if (revoking === null) return;
    try {
      await revoke.mutateAsync(revoking);
      setRevoking(null);
    } catch {
      // The dialog stays open: the person decides whether to retry or cancel,
      // rather than the failure silently closing their confirmation.
      toast.push('error', 'That session could not be signed out.');
    }
  };

  const columns: Column<SessionSummary>[] = [
    {
      key: 'createdAt',
      header: 'Signed in',
      render: (session) => (
        <span className="flex items-center gap-[var(--space-2)] text-body">
          {when(session.createdAt)}
          {session.current ? (
            <Badge size="sm" tone="progress">
              This device
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'lastUsedAt',
      header: 'Last used',
      render: (session) => <span className="text-muted">{when(session.lastUsedAt)}</span>,
    },
    {
      key: 'fingerprint',
      header: 'Fingerprint',
      kind: 'mono',
      render: (session) => <span className="text-sm text-muted">{fingerprint(session)}</span>,
    },
    {
      key: 'actions',
      header: '',
      kind: 'actions',
      width: '7rem',
      render: (session) => (
        <Button variant="secondary" size="sm" onClick={() => setRevoking(session)}>
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <section aria-labelledby="account-sessions-heading" className="mb-10">
      <h2 id="account-sessions-heading" className="mb-3 text-sm font-semibold text-body">
        Where you are signed in
      </h2>

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : null}

      {query.isError && !notFound ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}

      {query.data ? (
        <Table
          caption="Devices and browsers currently signed in to this account"
          columns={columns}
          rows={query.data}
          rowKey={(session) => session.familyId}
          density="compact"
        />
      ) : null}

      <ConfirmDialog
        open={revoking !== null}
        title={revoking?.current ? 'Sign out of this device?' : 'Sign out that device?'}
        body={
          revoking?.current
            ? 'This is the session you are using right now. Confirming signs you out here immediately and returns you to the sign-in screen.'
            : 'That device or browser will be signed out and will need the password to get back in.'
        }
        confirmLabel={revoking?.current ? 'Sign me out' : 'Sign it out'}
        destructive
        busy={revoke.isPending}
        onConfirm={() => void confirmRevoke()}
        onCancel={() => setRevoking(null)}
      />
    </section>
  );
}
